(() => {
  if (customElements.get('urban-size-guide')) return;
  const rememberedInputs = new Map();

  class UrbanSizeGuide extends HTMLElement {
    static recommendation({ unit, cm, feet, inches }) {
      const whole = (value) => /^\d+$/.test(String(value).trim()) && Number.isInteger(Number(value));
      if (unit === 'cm') {
        if (String(cm).trim() === '') return { message: 'Enter your height for length guidance.' };
        if (!whole(cm)) return { invalid: true, message: 'Enter your height in whole centimetres.' };
        const height = Number(cm);
        if (height < 152 || height > 180) return { invalid: true, message: 'This length guide covers 152-180 cm. Ask us about other heights.' };
        return { size: height <= 163 ? 'S/M' : 'M/L' };
      }
      if (String(feet).trim() === '' || String(inches).trim() === '') return { message: 'Enter both feet and inches for length guidance.' };
      if (!whole(feet) || !whole(inches) || Number(inches) > 11) return { invalid: true, message: 'Enter whole feet and inches, with inches from 0 to 11.' };
      const height = Number(feet) * 12 + Number(inches);
      if (height < 60 || height > 71) return { invalid: true, message: 'This length guide covers 5 ft 0 to 5 ft 11. Ask us about other heights.' };
      return { size: height <= 64 ? 'S/M' : 'M/L' };
    }

    connectedCallback() {
      this.unit = this.querySelector('[data-unit]');
      this.cm = this.querySelector('[data-cm]');
      this.feet = this.querySelector('[data-feet]');
      this.inches = this.querySelector('[data-inches]');
      this.result = this.querySelector('[data-result]');
      this.apply = this.querySelector('[data-apply]');
      this.root = this.closest('[id^="shopify-section-"]');
      if (!this.unit || !this.cm || !this.feet || !this.inches || !this.result || !this.apply || !this.root) return;
      try {
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
        this.options = JSON.parse(this.querySelector('[data-options]').textContent);
      } catch {
        this.result.textContent = 'Size guidance is unavailable. Please ask us about fit.';
        return;
      }
      this.key = this.root.id + ':' + this.dataset.productId;
      const saved = rememberedInputs.get(this.key);
      if (saved) {
        this.unit.value = saved.unit;
        this.cm.value = saved.cm;
        this.feet.value = saved.feet;
        this.inches.value = saved.inches;
      }
      this.onInput = () => this.update();
      this.onApply = () => this.applySize();
      this.onVariantChange = (event) => {
        const picker = event.target.closest('block-variant-picker');
        if (picker?.dataset.productId === this.dataset.productId) this.update();
      };
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onInput);
      this.apply.addEventListener('click', this.onApply);
      this.root.addEventListener('change', this.onVariantChange);
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('change', this.onInput);
      this.apply?.removeEventListener('click', this.onApply);
      this.root?.removeEventListener('change', this.onVariantChange);
    }

    picker() {
      return this.root.querySelector('block-variant-picker[data-product-id="' + this.dataset.productId + '"]');
    }

    selection(name) {
      const picker = this.picker();
      if (!picker) return '';
      const inputs = [...picker.querySelectorAll('input[type="radio"]')];
      const selected = inputs.find((input) => input.name.toLowerCase() === name && input.checked);
      if (selected) return selected.value;
      const select = [...picker.querySelectorAll('select')].find((input) => input.name.toLowerCase() === name);
      return select?.value || '';
    }

    update() {
      const values = { unit: this.unit.value, cm: this.cm.value, feet: this.feet.value, inches: this.inches.value };
      rememberedInputs.set(this.key, values);
      this.querySelector('[data-cm-field]').hidden = values.unit !== 'cm';
      this.querySelector('[data-feet-fields]').hidden = values.unit === 'cm';
      const recommendation = UrbanSizeGuide.recommendation(values);
      for (const input of [this.cm, this.feet, this.inches]) {
        input.setAttribute('aria-invalid', String(Boolean(recommendation.invalid && (values.unit === 'cm' ? input === this.cm : input !== this.cm))));
      }
      this.recommendedSize = recommendation.size || '';
      this.apply.disabled = true;
      this.apply.textContent = 'Select a height first';
      if (!this.recommendedSize) {
        this.result.textContent = recommendation.message;
        return;
      }
      const colorIndex = this.options.findIndex((name) => ['color', 'colour'].includes(name.toLowerCase()));
      const sizeIndex = this.options.findIndex((name) => name.toLowerCase() === 'size');
      const color = this.selection('color') || this.selection('colour');
      const variant = this.variants.find((item) => item.options[sizeIndex] === this.recommendedSize && (colorIndex < 0 || item.options[colorIndex] === color));
      if (!variant?.available) {
        this.result.textContent = this.recommendedSize + ' is the length guide for this height, but is unavailable in ' + (color || 'this selection') + '.';
        this.apply.textContent = 'Not available in this colour';
        return;
      }
      const alreadySelected = this.selection('size') === this.recommendedSize;
      this.result.textContent = this.recommendedSize + ' is the length guide for this height.' + (alreadySelected ? ' This size is already selected.' : ' Your colour stays ' + color + '.');
      this.apply.textContent = alreadySelected ? this.recommendedSize + ' selected' : 'Select ' + this.recommendedSize;
      this.apply.disabled = alreadySelected;
    }

    applySize() {
      this.update();
      if (this.apply.disabled || !this.recommendedSize) return;
      const picker = this.picker();
      const input = [...picker.querySelectorAll('input[type="radio"]')].find((item) => item.name.toLowerCase() === 'size' && item.value === this.recommendedSize);
      if (input) {
        input.click();
      } else {
        const select = [...picker.querySelectorAll('select')].find((item) => item.name.toLowerCase() === 'size');
        if (!select) return;
        select.value = this.recommendedSize;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  customElements.define('urban-size-guide', UrbanSizeGuide);
})();
